import React from "react";
import { UseFormReturn } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Briefcase, Users, Target, Award } from "lucide-react";
import { ExpertApplicationData } from "./schema";

interface ExperienceStepProps {
  form: UseFormReturn<ExpertApplicationData>;
  onNext: () => void;
  onPrev: () => void;
}

export function ExperienceStep({ 
  form, 
  onNext,
  onPrev
}: ExperienceStepProps) {
  const { register, formState: { errors } } = form;

  return (
    <div className="space-y-6">
      <Card className="border-blue-200 bg-blue-50">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-blue-800">
            <Briefcase className="w-5 h-5" />
            Professional Experience & Approach
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-blue-700">
            Help us understand your professional background and how you work with families. 
            This information will help families choose the right expert for their needs.
          </p>
        </CardContent>
      </Card>

      {/* Professional Bio */}
      <div>
        <Label htmlFor="bio" className="flex items-center gap-2">
          <Users className="w-4 h-4" />
          Professional Bio *
        </Label>
        <div className="text-sm text-gray-600 mb-2">
          <ul className="list-disc list-inside space-y-1">
            <li>What is your professional background and qualifications?</li>
            <li>What kinds of families or cases do you typically help?</li>
            <li>What makes your approach unique or effective?</li>
            <li>What outcomes have you helped families achieve?</li>
          </ul>
        </div>
        <Textarea 
          id="bio"
          {...register("bio")}
          placeholder="Share your professional background, the types of families you help, and what makes your approach unique..."
          rows={6}
        />
        {errors.bio && (
          <p className="text-red-600 text-sm mt-1">{errors.bio.message}</p>
        )}
        {/* AI Generate Bio Button */}
        <AIGenerateBioButton form={form} />
      </div>

      {/* Key Achievements */}
      <div>
        <Label className="flex items-center gap-2">
          <Award className="w-4 h-4" />
          Key Achievements & Success Stories
        </Label>
        <p className="text-sm text-gray-600 mb-2">
          Share some examples of how you've helped families (without identifying details)
        </p>
        <Textarea 
          placeholder="Describe 2-3 specific cases or achievements that demonstrate your expertise and impact..."
          rows={4}
        />
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between">
        <Button type="button" variant="outline" onClick={onPrev}>
          Back
        </Button>
        <Button type="button" onClick={onNext} className="bg-grandpal-green hover:bg-grandpal-green/90">
          Continue to Pricing
        </Button>
      </div>
    </div>
  );
}

function AIGenerateBioButton({ form }: { form: UseFormReturn<ExpertApplicationData> }) {
  const [loading, setLoading] = React.useState(false);
  const { setValue, getValues } = form;

  const handleGenerate = async () => {
    setLoading(true);
    // Gather relevant info
    const values = getValues();
    // Stub: Replace with real AI call
    const bio = await fakeAIGenerateBio(values);
    setValue("bio", bio, { shouldValidate: true });
    setLoading(false);
  };

  return (
    <div className="mt-2 flex items-center gap-2">
      <Button type="button" onClick={handleGenerate} disabled={loading} size="sm" variant="outline">
        {loading ? "Generating..." : "Generate Bio"}
      </Button>
      <span className="text-xs text-gray-500">suggest a professional bio based on your info</span>
    </div>
  );
}

// Stub for AI generation
async function fakeAIGenerateBio(values: any): Promise<string> {
  // Use only non-contact info: name, title, yearsExperience, specializations, availability
  const { fullName, professionalTitle, yearsExperience, specializations = [], availability = [] } = values;
  const specList = specializations.length > 0 ? specializations.join(", ") : "various areas";
  const availList = availability.length > 0 ? availability.join(", ") : "flexible times";

  // Variance helpers
  const greetings = [
    `Hello there, my name is ${fullName} and it's a pleasure to meet you.`,
    `Hi, I'm ${fullName}. Thank you for considering me to support your family.`,
    `Greetings! I'm ${fullName}, and I'm delighted to connect with you.`,
    `Welcome! My name is ${fullName}. I'm here to help you and your loved ones.`,
  ];
  const intros = [
    `I would be delighted to support you and your family with my ${yearsExperience} years of experience as a ${professionalTitle}.`,
    `With ${yearsExperience} years of experience as a ${professionalTitle}, I am passionate about helping families like yours.`,
    `As a ${professionalTitle} with ${yearsExperience} years of experience, I understand the challenges families face.`,
    `Bringing ${yearsExperience} years of expertise as a ${professionalTitle}, I am committed to guiding you through your care journey.`,
  ];
  const specLines = [
    `I specialize in ${specList}, and am available for consultations at ${availList}.`,
    `My main areas of expertise include ${specList}. I offer flexible consultation times: ${availList}.`,
    `You can count on my knowledge in ${specList}, with availability at ${availList}.`,
    `My focus areas are ${specList}, and I can meet with you at ${availList}.`,
  ];
  const closings = [
    `My goal is to make your care journey as smooth and positive as possible, and I look forward to helping you achieve the best outcomes for your loved ones.`,
    `I'm here to make this process easier for you and your family, and I hope to be a trusted partner on your journey.`,
    `Supporting families through important decisions is my passion, and I look forward to working together.`,
    `Let's work together to ensure the best possible care and support for your loved ones.`,
  ];

  function pick(arr: string[]) {
    return arr[Math.floor(Math.random() * arr.length)];
  }

  return `${pick(greetings)} ${pick(intros)} ${pick(specLines)} ${pick(closings)}`;
}
